// Shared WebAuthn ceremony error-message policy for the marketplace customer
// embed — fix-round-2 I-3. Extracted from SecurityPanel.tsx, which was the
// only one of the THREE ceremony call sites (SecurityPanel's Add-a-Passkey,
// SignInPanel's passkey sign-in, PasskeyEnrollPrompt's post-signup offer) to
// apply this treatment. The other two collapsed every non-cancellation
// failure into generic "Please try again" copy — wrong for a PERMANENT
// failure. A `SecurityError` (thrown when the ceremony's origin doesn't
// match the relying party — see the RP-ID mismatch this same fix round's I-1
// closes) can never be fixed by retrying, and "Please try again" invites the
// one action guaranteed not to work.
//
// Extracted to a SHARED module rather than copied a third time: three copies
// of a message-name allow-list is how they drift (one file gains a name the
// others don't, or a fallback string diverges) — CLAUDE.md §4, consistency >
// preference. Lives under embed/marketplace/ (not embed-shared/) because all
// three consumers are marketplace-embed files; embed-shared/passkey.ts's own
// header explicitly scopes itself to "the two universal, stateless probes
// every consumer needs... plus the base64url codec" — this error-message
// POLICY is a different kind of concern (product copy, not a WebAuthn
// primitive) and does not belong bolted onto that file's narrower contract.
//
// Operator ruling R2 is binding here: email-OTP sign-in is ALWAYS available
// and is NEVER presented as replaced or degraded. Every string this function
// can return — including a raw browser-supplied name.message — must never
// imply the customer is locked out of their account; a permanent PASSKEY
// failure is not an account lockout, since OTP remains the sign-in path
// throughout. This module never invents copy of its own beyond the caller's
// fallback string; it only decides whether to surface the browser's own
// message or a caller-supplied fallback — nothing here writes "you cannot
// sign in" phrasing.

import { ApiError } from "../../embed-shared/api-client";

/**
 * WebAuthn spec ceremony-failure DOMException names
 * (https://w3c.github.io/webauthn/, CredentialsContainer create()/get()).
 * Checking `.name` against this allow-list — rather than `err instanceof
 * DOMException` — matches how every consumer's tests construct ceremony
 * fixtures (a plain `Error` with `.name` set) and sidesteps jsdom's
 * DOMException not subclassing Error the way a real browser's does (see
 * passkeys.test.ts's header note on this exact jsdom fact). Deliberately an
 * ALLOW-list, not "any Error with a message": a genuine programming error
 * elsewhere in a caller's call chain (e.g. a stray TypeError) has `.name ===
 * "Error"`/"TypeError", is NOT in this set, and must fall back to the
 * caller's generic copy rather than leak its internal message to the
 * customer.
 */
const WEBAUTHN_CEREMONY_ERROR_NAMES = new Set([
  "NotAllowedError",
  "InvalidStateError",
  "NotSupportedError",
  "SecurityError",
  "AbortError",
  "ConstraintError",
  "UnknownError",
  "NetworkError",
  "TimeoutError",
  "EncodingError",
]);

/**
 * Resolve a user-facing message for a WebAuthn ceremony failure (register or
 * sign-in). NEVER call this for a cancellation (`err.name ===
 * "NotAllowedError"` from the user dismissing the prompt) — that is a normal
 * outcome, not a failure to explain; every call site checks cancellation
 * first and never reaches here for it.
 *
 * - An `ApiError` (the ceremony's finish call was rejected server-side)
 *   returns its own message — already customer-safe copy from the backend's
 *   anti-enumeration-uniform error responses.
 * - A browser-thrown ceremony failure whose `.name` is on the WebAuthn
 *   allow-list above returns ITS message — a short, specific, English reason
 *   (e.g. "SecurityError: The relying party ID is not a registrable domain
 *   suffix of, or equal to, the current domain."), not a stack-trace
 *   fragment, because it originates from the browser's own WebAuthn
 *   implementation rather than the network.
 * - Anything else (a non-WebAuthn programming error, or an Error with no
 *   message) returns the caller's `fallback` — never leaked verbatim.
 */
export function ceremonyMessageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message && WEBAUTHN_CEREMONY_ERROR_NAMES.has(err.name)) {
    return err.message;
  }
  return fallback;
}
